// Tour-route + open-week input schemas.
// One schema, two consumers: server validates via @hono/zod-validator;
// SPA validates via mantine-form-zod-resolver.

import { z } from 'zod';

import { isoDateSchema, richTextSchema } from './common.js';

const TOUR_ROUTE_NAME_MAX = 80;
const TOUR_ROUTE_CITY_MAX = 80;
const TOUR_ROUTE_MAX_CITIES = 6;
const TOUR_ROUTE_MIN_CITIES = 1;
// A tour route's [from,to] window bounds the artist-availability resolver, which
// iterates the span day-by-day in memory (browse Smart Match + tour-route-match
// sweeper). 365d is far beyond any real tour and keeps that iteration bounded.
const TOUR_ROUTE_MAX_DURATION_DAYS = 365;
const OPEN_WEEK_NOTES_MAX = 300;
const CORRIDOR_RADIUS_MIN_KM = 0;
const CORRIDOR_RADIUS_MAX_KM = 150;
const CORRIDOR_RADIUS_DEFAULT_KM = 50;

/**
 * Each city leg accepts either a plain string (legacy / quick add) or an
 * object with optional per-leg dates. A preprocess step normalises plain
 * strings to `{ name }` so the schema after that point is uniformly object-
 * shaped. The route handler then converts ISO dates → ms before storage.
 */
const cityLegInputSchema = z
  .preprocess(
    (input) => (typeof input === 'string' ? { name: input } : input),
    z
      .object({
        name: richTextSchema(TOUR_ROUTE_CITY_MAX).transform((s) =>
          s.trim().toLowerCase(),
        ),
        country: z.string().regex(/^[A-Z]{2}$/).optional(),
        from_date: isoDateSchema.optional(),
        to_date: isoDateSchema.optional(),
      })
      .refine(
        (l) => {
          if (l.from_date === undefined || l.to_date === undefined) return true;
          return l.from_date <= l.to_date;
        },
        {
          message: "End date must be on or after this city's start date.",
          path: ['to_date'],
        },
      ),
  );

export type CityLegInput = z.infer<typeof cityLegInputSchema>;

const cityArraySchema = z
  .array(cityLegInputSchema)
  .min(TOUR_ROUTE_MIN_CITIES, { message: 'Add at least one city.' })
  .max(TOUR_ROUTE_MAX_CITIES, {
    message: `That's a lot of cities — keep it to ${TOUR_ROUTE_MAX_CITIES}.`,
  })
  .transform((arr) => arr.filter((leg) => leg.name.length > 0));

const corridorRadiusSchema = z
  .number()
  .int()
  .min(CORRIDOR_RADIUS_MIN_KM)
  .max(CORRIDOR_RADIUS_MAX_KM);

const dateRangeRefine = (data: { from_date: string; to_date: string }) =>
  data.from_date <= data.to_date;

const MS_PER_DAY = 86_400_000;

/**
 * Tour-route windows must fit inside a bounded horizon. isoDateSchema strings
 * ('YYYY-MM-DD') parse to UTC midnight, so the diff is exact whole days. Returns
 * true when from > to (that's dateRangeRefine's job to reject, not this one's).
 */
const withinMaxDuration = (from: string, to: string): boolean =>
  (Date.parse(to) - Date.parse(from)) / MS_PER_DAY <= TOUR_ROUTE_MAX_DURATION_DAYS;

/**
 * Per-leg dates, when present, must fit inside the route envelope.
 */
const legsFitEnvelopeRefine = (
  d: {
    from_date?: string | undefined;
    to_date?: string | undefined;
    cities?: CityLegInput[] | undefined;
  },
): boolean => {
  if (d.cities === undefined) return true;
  for (const leg of d.cities) {
    if (d.from_date !== undefined && leg.from_date !== undefined) {
      if (leg.from_date < d.from_date) return false;
    }
    if (d.to_date !== undefined && leg.to_date !== undefined) {
      if (leg.to_date > d.to_date) return false;
    }
  }
  return true;
};

export const createTourRouteInputSchema = z
  .object({
    name: richTextSchema(TOUR_ROUTE_NAME_MAX).refine((s) => s.trim().length > 0, {
      message: 'Give the route a short name.',
    }),
    cities: cityArraySchema,
    from_date: isoDateSchema,
    to_date: isoDateSchema,
    notify_on_match: z.boolean().default(true),
    corridor_radius_km: corridorRadiusSchema.default(CORRIDOR_RADIUS_DEFAULT_KM),
  })
  .refine(dateRangeRefine, {
    message: 'End date must be on or after the start date.',
    path: ['to_date'],
  })
  .refine((d) => withinMaxDuration(d.from_date, d.to_date), {
    message: `A tour route can span at most ${TOUR_ROUTE_MAX_DURATION_DAYS} days.`,
    path: ['to_date'],
  })
  .refine(legsFitEnvelopeRefine, {
    message: "A city's dates must fit inside the route's window.",
    path: ['cities'],
  });
export type CreateTourRouteInput = z.infer<typeof createTourRouteInputSchema>;

export const updateTourRouteInputSchema = z
  .object({
    name: richTextSchema(TOUR_ROUTE_NAME_MAX)
      .refine((s) => s.trim().length > 0, { message: 'Give the route a short name.' })
      .optional(),
    cities: cityArraySchema.optional(),
    from_date: isoDateSchema.optional(),
    to_date: isoDateSchema.optional(),
    is_active: z.boolean().optional(),
    notify_on_match: z.boolean().optional(),
    corridor_radius_km: corridorRadiusSchema.optional(),
  })
  .refine(
    (d) => {
      if (d.from_date === undefined || d.to_date === undefined) return true;
      return d.from_date <= d.to_date;
    },
    { message: 'End date must be on or after the start date.', path: ['to_date'] },
  )
  .refine(
    (d) => {
      if (d.from_date === undefined || d.to_date === undefined) return true;
      return withinMaxDuration(d.from_date, d.to_date);
    },
    {
      message: `A tour route can span at most ${TOUR_ROUTE_MAX_DURATION_DAYS} days.`,
      path: ['to_date'],
    },
  )
  .refine(legsFitEnvelopeRefine, {
    message: "A city's dates must fit inside the route's window.",
    path: ['cities'],
  });
export type UpdateTourRouteInput = z.infer<typeof updateTourRouteInputSchema>;

export const createOpenWeekInputSchema = z
  .object({
    from_date: isoDateSchema,
    to_date: isoDateSchema,
    notes: richTextSchema(OPEN_WEEK_NOTES_MAX).optional(),
  })
  .refine(dateRangeRefine, {
    message: 'End date must be on or after the start date.',
    path: ['to_date'],
  });
export type CreateOpenWeekInput = z.infer<typeof createOpenWeekInputSchema>;

export const PLANNING_LIMITS = {
  tourRouteNameMax: TOUR_ROUTE_NAME_MAX,
  tourRouteMaxCities: TOUR_ROUTE_MAX_CITIES,
  tourRouteMinCities: TOUR_ROUTE_MIN_CITIES,
  tourRouteMaxDurationDays: TOUR_ROUTE_MAX_DURATION_DAYS,
  openWeekNotesMax: OPEN_WEEK_NOTES_MAX,
  corridorRadiusMinKm: CORRIDOR_RADIUS_MIN_KM,
  corridorRadiusMaxKm: CORRIDOR_RADIUS_MAX_KM,
  corridorRadiusDefaultKm: CORRIDOR_RADIUS_DEFAULT_KM,
} as const;
